import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { api, API_URL } from '../../api/client';
import { getRelationshipSuggestions, updateCustomRelationship } from '../../api/analytics';
import StandardRelationshipSuggestionsModal from './StandardRelationshipSuggestionsModal';

/**
 * RelationshipsTab - View auto-detected relationships and manage custom cross-source relationships.
 *
 * Auto-detected: fetched from /api/v1/analytics/relationships/lookup and /child
 * Custom: fetched from /api/v1/analytics/relationships/custom (CRUD)
 */
export default function RelationshipsTab() {
  const { activeTenant } = useSelector((state) => state.auth);

  // Auto-detected relationships
  const [lookupRels, setLookupRels] = useState([]);
  const [childRels, setChildRels] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoError, setAutoError] = useState(null);

  // Custom relationships
  const [customRels, setCustomRels] = useState([]);
  const [customLoading, setCustomLoading] = useState(false);
  const [customError, setCustomError] = useState(null);

  // Add custom relationship form
  const [form, setForm] = useState({
    parent_object: '',
    parent_field: '',
    child_object: '',
    child_field: '',
    relationship_name: '',
    parent_to_child_name: '',
  });
  const [formError, setFormError] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  // Deleting state
  const [deletingId, setDeletingId] = useState(null);

  // Edit modal state
  const [editingRel, setEditingRel] = useState(null); // the rel object being edited
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null); // "Saved. N views updated."

  // Semantic-change confirmation (409 on edit)
  const [semanticConflict, setSemanticConflict] = useState(null); // { views, pendingBody }
  // Delete-blocked confirmation (409 on delete)
  const [blockedDelete, setBlockedDelete] = useState(null); // { views }

  // Standard relationship suggestions
  const [suggestions, setSuggestions] = useState(null);
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  // selectedPairing is the specific pairing the user clicked "Review & Create" on
  const [selectedPairing, setSelectedPairing] = useState(null);

  // Info modal
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef(null);
  useEffect(() => {
    if (!showInfo) return;
    const handleKey = (e) => { if (e.key === 'Escape') setShowInfo(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [showInfo]);

  const tenantId = activeTenant?.tenant_id;

  const fetchAutoDetected = useCallback(async () => {
    if (!tenantId) return;
    setAutoLoading(true);
    setAutoError(null);
    try {
      const [lookupData, childData] = await Promise.all([
        api.get(`${API_URL}/api/v1/analytics/relationships/lookup`, { tenantId }),
        api.get(`${API_URL}/api/v1/analytics/relationships/child`, { tenantId }),
      ]);
      setLookupRels(Array.isArray(lookupData) ? lookupData : (lookupData?.relationships || []));
      setChildRels(Array.isArray(childData) ? childData : (childData?.relationships || []));
    } catch (err) {
      setAutoError(err.message || 'Failed to load auto-detected relationships');
    } finally {
      setAutoLoading(false);
    }
  }, [tenantId]);

  const fetchCustomRels = useCallback(async () => {
    if (!tenantId) return;
    setCustomLoading(true);
    setCustomError(null);
    try {
      const data = await api.get(`${API_URL}/api/v1/analytics/relationships/custom`, { tenantId });
      setCustomRels(Array.isArray(data) ? data : (data?.relationships || []));
    } catch (err) {
      setCustomError(err.message || 'Failed to load custom relationships');
    } finally {
      setCustomLoading(false);
    }
  }, [tenantId]);

  const fetchSuggestions = useCallback(async () => {
    if (!tenantId) return;
    try {
      const data = await getRelationshipSuggestions();
      setSuggestions(data);
    } catch {
      // Non-fatal: suggestions are a helpful hint, not a required feature
    }
  }, [tenantId]);

  useEffect(() => {
    // Run all three fetches in parallel — they are independent.
    Promise.all([fetchAutoDetected(), fetchCustomRels(), fetchSuggestions()]);
  }, [fetchAutoDetected, fetchCustomRels, fetchSuggestions]);

  // Compute whether all suggestions for all pairings have been dismissed.
  // Dismiss key per pairing: std-rel-dismissed:{tenantId}:{childDsId}:{parentDsId}
  const isDismissed = (pairing) => {
    if (!tenantId) return false;
    const key = `std-rel-dismissed:${tenantId}:${pairing.child_ds.data_source_id}:${pairing.parent_ds.data_source_id}`;
    return localStorage.getItem(key) === 'true';
  };

  const handleDismiss = (pairing) => {
    if (!tenantId) return;
    const key = `std-rel-dismissed:${tenantId}:${pairing.child_ds.data_source_id}:${pairing.parent_ds.data_source_id}`;
    localStorage.setItem(key, 'true');
    // Refetch so the banner re-evaluates via isDismissed — no null flash needed.
    fetchSuggestions();
  };

  // A pairing has "pending" items if any suggestion has already_exists=false
  const hasPendingItems = (pairing) =>
    pairing.suggestions.some(s => !s.already_exists);

  // Filter pairings that should show a banner: pending items + not dismissed
  const visiblePairings = suggestions?.pairings?.filter(
    p => hasPendingItems(p) && !isDismissed(p)
  ) || [];

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleAddRelationship = async (e) => {
    e.preventDefault();
    const { parent_object, parent_field, child_object, child_field, relationship_name, parent_to_child_name } = form;

    if (!parent_object.trim() || !parent_field.trim() || !child_object.trim() || !child_field.trim()) {
      setFormError('Parent object, parent field, child object, and child field are required');
      return;
    }
    if (!relationship_name.trim() && !parent_to_child_name.trim()) {
      setFormError('At least one relationship name (Child→Parent or Parent→Child) is required');
      return;
    }

    setFormSaving(true);
    setFormError(null);
    try {
      await api.post(`${API_URL}/api/v1/analytics/relationships/custom`, {
        parent_object: parent_object.trim(),
        parent_field: parent_field.trim(),
        child_object: child_object.trim(),
        child_field: child_field.trim(),
        relationship_name: relationship_name.trim(),
        parent_to_child_name: parent_to_child_name.trim(),
      }, { tenantId });

      setForm({
        parent_object: '',
        parent_field: '',
        child_object: '',
        child_field: '',
        relationship_name: '',
        parent_to_child_name: '',
      });

      await fetchCustomRels();
    } catch (err) {
      setFormError(err.message || 'Failed to create relationship');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteRelationship = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`${API_URL}/api/v1/analytics/relationships/custom/${encodeURIComponent(id)}`, { tenantId });
      setCustomRels(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      if (err.status === 409 && err.data?.error === 'has_dependents') {
        setBlockedDelete({ views: err.data.affected_views ?? [] });
      } else {
        setCustomError(err.message || 'Failed to delete relationship');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const openEditModal = (rel) => {
    setEditingRel(rel);
    setEditForm({
      parent_object:        rel.parent_object || '',
      parent_field:         rel.parent_field || '',
      child_object:         rel.child_object || '',
      child_field:          rel.child_field || '',
      relationship_name:    rel.relationship_name || '',
      parent_to_child_name: rel.parent_to_child_name || '',
    });
    setEditError(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    setEditError(null);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    const { parent_object, parent_field, child_object, child_field, relationship_name, parent_to_child_name } = editForm;
    if (!parent_object.trim() || !parent_field.trim() || !child_object.trim() || !child_field.trim()) {
      setEditError('Parent object, parent field, child object, and child field are required');
      return;
    }
    if (!relationship_name.trim() && !parent_to_child_name.trim()) {
      setEditError('At least one relationship name (Child→Parent or Parent→Child) is required');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    const body = {
      parent_object:        parent_object.trim(),
      parent_field:         parent_field.trim(),
      child_object:         child_object.trim(),
      child_field:          child_field.trim(),
      relationship_name:    relationship_name.trim(),
      parent_to_child_name: parent_to_child_name.trim(),
    };
    try {
      const result = await updateCustomRelationship(editingRel.id, body, { tenantId });
      setEditingRel(null);
      setEditForm(null);
      if (result?.views_repaired > 0) {
        setEditSuccess(`Saved. ${result.views_repaired} view${result.views_repaired === 1 ? '' : 's'} updated to use the new name.`);
        setTimeout(() => setEditSuccess(null), 5000);
      }
      await fetchCustomRels();
    } catch (err) {
      if (err.status === 409 && err.data?.error === 'semantic_change_requires_confirmation') {
        setSemanticConflict({ views: err.data.affected_views ?? [], pendingBody: body });
      } else {
        setEditError(err.message || 'Failed to update relationship');
      }
    } finally {
      setEditSaving(false);
    }
  };

  const handleSemanticConfirm = async () => {
    if (!semanticConflict || !editingRel) return;
    setEditSaving(true);
    try {
      await updateCustomRelationship(editingRel.id, { ...semanticConflict.pendingBody, force: true }, { tenantId });
      setSemanticConflict(null);
      setEditingRel(null);
      setEditForm(null);
      await fetchCustomRels();
    } catch (err) {
      setEditError(err.message || 'Failed to update relationship');
      setSemanticConflict(null);
    } finally {
      setEditSaving(false);
    }
  };

  const getSourceBadge = (sourceType) => {
    if (!sourceType) return null;
    const normalized = sourceType.toLowerCase();
    if (normalized === 'salesforce' || normalized === 'sf') {
      return <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">SF</span>;
    }
    if (normalized === 'hubspot' || normalized === 'hs') {
      return <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-700">HubSpot</span>;
    }
    return <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">{sourceType}</span>;
  };

  // Join lookup and child relationships into unified 6-field records.
  // Lookup gives us: child_object, child_field, child→parent name, parent_object, source info.
  // Child gives us: parent→child name (the subquery alias, e.g. "Contacts").
  // Join key: lookup.source_object === child.child_object && lookup.lookup_field === child.field
  const allAutoRels = lookupRels.map(lookup => {
    const match = childRels.find(
      c => c.child_object === lookup.source_object && c.field === lookup.lookup_field
    );
    return {
      parent_object: lookup.target_object,
      parent_field: 'Id',
      child_object: lookup.source_object,
      child_field: lookup.lookup_field,
      child_to_parent_name: lookup.relationship_name,
      parent_to_child_name: match?.relationship_name || '',
      data_source_id: lookup.data_source_id,
      data_source_name: lookup.data_source_name,
      source_type: lookup.source_type,
    };
  });

  const RelationshipInfoModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowInfo(false)}>
      <div ref={infoRef} className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="relationships-info-modal-title" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 id="relationships-info-modal-title" className="text-lg font-bold text-gray-900">How Relationships Work</h2>
          <button onClick={() => setShowInfo(false)} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 space-y-6">
          <p className="text-sm text-gray-600">
            Each row encodes <strong>two</strong> SOQL traversal paths at once — one going up from child to parent,
            one going down from parent to child. Both paths come from the same foreign key field on the child object.
          </p>

          {/* Example row */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Example row</p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Parent Object','Parent Field','Child Object','Child Field','Child→Parent Name','Parent→Child Name','Source'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono font-semibold text-gray-900">Account</td>
                    <td className="px-3 py-2 font-mono text-gray-500">Id</td>
                    <td className="px-3 py-2 font-mono font-semibold text-gray-900">Contact</td>
                    <td className="px-3 py-2 font-mono text-gray-500">AccountId</td>
                    <td className="px-3 py-2 font-mono text-purple-700 font-semibold">Account</td>
                    <td className="px-3 py-2 font-mono text-indigo-700 font-semibold">Contacts</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">SF</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              The foreign key <code className="bg-gray-100 px-1 rounded">AccountId</code> on <strong>Contact</strong> points to <code className="bg-gray-100 px-1 rounded">Account.Id</code>.
              Salesforce names both traversal directions at field-definition time.
            </p>
          </div>

          {/* SOQL pattern 1: child → parent */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-3 h-3 rounded-full bg-purple-500"></span>
              <p className="text-sm font-semibold text-gray-800">
                Child → Parent &nbsp;·&nbsp; <span className="font-mono text-purple-700">Account</span> &nbsp;·&nbsp; dot notation
              </p>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Query the <strong>child</strong> object (<code className="bg-gray-100 px-1 rounded">Contact</code>) and reach up to the parent using
              the <span className="text-purple-700 font-semibold">Child→Parent Name</span> as a prefix.
              You can chain further: <code className="bg-gray-100 px-1 rounded">Account.Owner.Name</code>.
            </p>
            <pre className="bg-gray-900 text-green-300 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">{`SELECT Id, FirstName, LastName,
       Account.Name,          -- Child→Parent Name "Account" used as prefix
       Account.Industry,
       Account.BillingState
FROM Contact
WHERE Account.BillingState = 'CA'`}</pre>
          </div>

          {/* SOQL pattern 2: parent → child */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-3 h-3 rounded-full bg-indigo-500"></span>
              <p className="text-sm font-semibold text-gray-800">
                Parent → Child &nbsp;·&nbsp; <span className="font-mono text-indigo-700">Contacts</span> &nbsp;·&nbsp; subquery
              </p>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Query the <strong>parent</strong> object (<code className="bg-gray-100 px-1 rounded">Account</code>) and nest a subquery using
              the <span className="text-indigo-700 font-semibold">Parent→Child Name</span> in the <code className="bg-gray-100 px-1 rounded">FROM</code> clause.
              Note: this is the relationship name, not the object API name.
            </p>
            <pre className="bg-gray-900 text-green-300 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">{`SELECT Id, Name,
       (SELECT Id, FirstName, LastName, Email
        FROM Contacts)        -- Parent→Child Name "Contacts" used in FROM
FROM Account
WHERE Industry = 'Technology'`}</pre>
          </div>

          <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
            Relationship names are defined in Salesforce object metadata and can be looked up
            via <code className="bg-gray-100 px-1 rounded">describeSObjects()</code> or in Setup → Object Manager.
          </p>
        </div>
      </div>
    </div>
  );

  const relTableHead = (
    <thead className="bg-gray-50">
      <tr>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Object</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Field</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Child Object</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Child Field</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Child→Parent Name</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent→Child Name</th>
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
      </tr>
    </thead>
  );

  return (
    <div className="space-y-6">
      {/* Standard relationship suggestion banners — one per spiritually-paired DS pair */}
      {visiblePairings.map(pairing => {
        const pairingKey = `${pairing.child_ds.data_source_id}:${pairing.parent_ds.data_source_id}`;
        const pendingCount = pairing.suggestions.filter(s => !s.already_exists).length;
        return (
          <div
            key={pairingKey}
            data-testid={`standard-rel-suggestion-banner-${pairingKey}`}
            className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <span>&#128161;</span>
              <span>
                We found <strong>{pairing.child_ds.name}</strong> + <strong>{pairing.parent_ds.name}</strong>
                {' \u2014 '}
                set up {pendingCount} standard relationship{pendingCount === 1 ? '' : 's'}.
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid="standard-rel-review-btn"
                onClick={() => { setSelectedPairing(pairing); setShowSuggestionsModal(true); }}
                className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
              >
                Review &amp; Create
              </button>
              <button
                data-testid="standard-rel-dismiss-btn"
                onClick={() => handleDismiss(pairing)}
                className="px-3 py-1.5 bg-white text-amber-700 text-xs font-medium rounded border border-amber-300 hover:bg-amber-50"
              >
                Dismiss
              </button>
            </div>
          </div>
        );
      })}

      {/* Standard relationship suggestions modal — scoped to the selected pairing */}
      {showSuggestionsModal && selectedPairing && (
        <StandardRelationshipSuggestionsModal
          pairings={[selectedPairing]}
          onCreated={() => {
            setShowSuggestionsModal(false);
            setSelectedPairing(null);
            fetchCustomRels();
            fetchSuggestions();
          }}
          onClose={() => { setShowSuggestionsModal(false); setSelectedPairing(null); }}
        />
      )}

      {/* Custom Relationships */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-bold text-gray-900">Custom Relationships</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Define cross-source joins for FrankenQL queries (e.g., Salesforce Account linked to HubSpot Company)
          </p>
        </div>

        {/* Add form */}
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Add Relationship</h3>
          <form onSubmit={handleAddRelationship} className="space-y-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Parent Object</label>
                <input
                  type="text"
                  name="parent_object"
                  value={form.parent_object}
                  onChange={handleFormChange}
                  placeholder="e.g. Account"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Parent Field</label>
                <input
                  type="text"
                  name="parent_field"
                  value={form.parent_field}
                  onChange={handleFormChange}
                  placeholder="e.g. Id"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Child Object</label>
                <input
                  type="text"
                  name="child_object"
                  value={form.child_object}
                  onChange={handleFormChange}
                  placeholder="e.g. hs.companies"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Child Field</label>
                <input
                  type="text"
                  name="child_field"
                  value={form.child_field}
                  onChange={handleFormChange}
                  placeholder="e.g. salesforce_account_id"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Child→Parent Name</label>
                <input
                  type="text"
                  name="relationship_name"
                  value={form.relationship_name}
                  onChange={handleFormChange}
                  placeholder="e.g. SalesforceAccount"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Parent→Child Name</label>
                <input
                  type="text"
                  name="parent_to_child_name"
                  value={form.parent_to_child_name}
                  onChange={handleFormChange}
                  placeholder="e.g. HubSpotContacts"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                />
              </div>
            </div>

            {formError && (
              <p className="text-sm text-red-600">{formError}</p>
            )}

            <button
              type="submit"
              disabled={formSaving}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formSaving ? 'Adding...' : 'Add Relationship'}
            </button>
          </form>
        </div>

        {/* Custom rels table */}
        {customError && (
          <div className="p-4 bg-red-50 border-b border-red-100 text-sm text-red-700">
            {customError}
          </div>
        )}

        {customLoading && customRels.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading custom relationships...</div>
        ) : customRels.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-sm">No custom relationships defined yet. Use the form above to add one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="custom-rels-table">
            <table className="w-full text-sm">
              {relTableHead}
              <tbody className="divide-y divide-gray-100">
                {customRels.map((rel) => (
                  <tr key={rel.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-800">{rel.parent_object || '-'}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{rel.parent_field || '-'}</td>
                    <td className="px-4 py-3 font-mono text-gray-800">{rel.child_object || '-'}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{rel.child_field || '-'}</td>
                    <td className="px-4 py-3 font-mono text-purple-700">{rel.relationship_name || '-'}</td>
                    <td className="px-4 py-3 font-mono text-indigo-700">{rel.parent_to_child_name || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`edit-rel-btn-${rel.id}`}
                          onClick={() => openEditModal(rel)}
                          disabled={deletingId === rel.id}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          data-testid={`delete-rel-btn-${rel.id}`}
                          onClick={() => handleDeleteRelationship(rel.id)}
                          disabled={deletingId === rel.id}
                          className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                        >
                          {deletingId === rel.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Auto-Detected Relationships */}
      {showInfo && <RelationshipInfoModal />}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div>
              <h2 className="font-bold text-gray-900">Auto-Detected Relationships</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Relationships discovered from your synced data sources
              </p>
            </div>
            <button
              onClick={() => setShowInfo(true)}
              title="How relationships work"
              className="text-gray-400 hover:text-primary-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          <button
            onClick={fetchAutoDetected}
            disabled={autoLoading}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
          >
            {autoLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {autoError && (
          <div className="p-4 bg-red-50 border-b border-red-100 text-sm text-red-700">
            {autoError}
          </div>
        )}

        {autoLoading && allAutoRels.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading relationships...</div>
        ) : allAutoRels.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="mb-1 font-medium text-gray-700">No auto-detected relationships found</p>
            <p className="text-sm">Sync your data sources to detect field relationships automatically.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              {relTableHead}
              <tbody className="divide-y divide-gray-100">
                {allAutoRels.map((rel, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-800">{rel.parent_object || '-'}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{rel.parent_field || '-'}</td>
                    <td className="px-4 py-3 font-mono text-gray-800">{rel.child_object || '-'}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{rel.child_field || '-'}</td>
                    <td className="px-4 py-3 font-mono text-purple-700">{rel.child_to_parent_name || '-'}</td>
                    <td className="px-4 py-3 font-mono text-indigo-700">{rel.parent_to_child_name || '-'}</td>
                    <td className="px-4 py-3">{getSourceBadge(rel.source_type)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit relationship modal */}
      {editingRel && editForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => { setEditingRel(null); setEditForm(null); }}
        >
          <div
            data-testid="edit-rel-modal"
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-rel-modal-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h2 id="edit-rel-modal-title" className="text-lg font-bold text-gray-900">
                Edit Relationship
              </h2>
              <button
                onClick={() => { setEditingRel(null); setEditForm(null); }}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parent Object</label>
                  <input
                    type="text"
                    name="parent_object"
                    value={editForm.parent_object}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parent Field</label>
                  <input
                    type="text"
                    name="parent_field"
                    value={editForm.parent_field}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Child Object</label>
                  <input
                    type="text"
                    name="child_object"
                    value={editForm.child_object}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Child Field</label>
                  <input
                    type="text"
                    name="child_field"
                    value={editForm.child_field}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Child→Parent Name</label>
                  <input
                    type="text"
                    name="relationship_name"
                    value={editForm.relationship_name}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parent→Child Name</label>
                  <input
                    type="text"
                    name="parent_to_child_name"
                    value={editForm.parent_to_child_name}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>

              {editError && (
                <p className="text-sm text-red-600">{editError}</p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setEditingRel(null); setEditForm(null); }}
                  disabled={editSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="edit-rel-save-btn"
                  disabled={editSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success toast for view repairs */}
      {editSuccess && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-green-600 text-white text-sm rounded-lg shadow-lg">
          {editSuccess}
        </div>
      )}

      {/* Semantic-change confirmation dialog */}
      {semanticConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Confirm semantic change</h3>
            <p className="text-sm text-gray-600 mb-3">
              Changing the join fields affects {semanticConflict.views.length} saved view{semanticConflict.views.length === 1 ? '' : 's'}.
              Those views will continue to run but may return incorrect results until you update their queries.
            </p>
            {semanticConflict.views.length > 0 && (
              <ul className="mb-4 text-xs text-gray-500 space-y-0.5 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                {semanticConflict.views.map(v => (
                  <li key={v.id}>{v.name}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setSemanticConflict(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSemanticConfirm}
                disabled={editSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {editSaving ? 'Saving…' : 'Proceed anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete-blocked dialog */}
      {blockedDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Cannot delete relationship</h3>
            <p className="text-sm text-gray-600 mb-3">
              This relationship is referenced by {blockedDelete.views.length} saved view{blockedDelete.views.length === 1 ? '' : 's'}.
              Remove the relationship from those views before deleting.
            </p>
            {blockedDelete.views.length > 0 && (
              <ul className="mb-4 text-xs text-gray-500 space-y-0.5 max-h-32 overflow-y-auto border border-gray-200 rounded p-2">
                {blockedDelete.views.map(v => (
                  <li key={v.id}>{v.name}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => setBlockedDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
